import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useToday, useProgress, formatLongDate } from "@/hooks/useCalendar";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, Leaf, Flame, HeartHandshake, Ruler, Sparkles, Settings, AlertCircle, Copy, Gift, RotateCcw } from "lucide-react";

export default function Dashboard() {
  const { user, isSubscribed, refresh } = useAuth();
  const { data: today, loading } = useToday();
  const { has, toggle } = useProgress();
  const navigate = useNavigate();
  const [managing, setManaging] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [ref, setRef] = useState(null);

  useEffect(() => { api.get("/referrals/me").then((r) => setRef(r.data)).catch(() => {}); }, []);

  const handleManageClick = () => openPortal();

  const openPortal = async () => {
    setManaging(true);
    try {
      const { data } = await api.post("/subscription/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (_e) {
      navigate("/pricing");
    } finally { setManaging(false); }
  };

  const reactivate = async () => {
    setReactivating(true);
    try {
      await api.post("/subscription/reactivate");
      await refresh();
      toast.success("Welcome back — your rhythm continues.");
    } catch (_e) {
      toast.error("Could not reactivate. Try the Stripe portal.");
    } finally { setReactivating(false); }
  };

  const cancelDate = user?.cancel_at ? new Date(user.cancel_at) : null;
  const showCancelBanner = user?.cancel_at_period_end && cancelDate;
  const referralLink = ref?.code ? `${window.location.origin}/auth?mode=register&ref=${ref.code}` : "";

  const activeDate = today?.active_date;
  const dinner = today?.day?.recipes?.find((r) => r.meal_type === "dinner");
  const movement = today?.day?.movements?.find((m) => m.tier === "expanding") || today?.day?.movements?.[0];
  const coaching = today?.day?.coaching;
  const completed = ["meal", "movement", "coaching"].filter((k) => has(activeDate, k)).length;

  return (
    <div className="space-y-16" data-testid="dashboard-root">
      {showCancelBanner && (
        <section className="border border-[hsl(var(--primary))] p-6 md:p-8 grid md:grid-cols-12 gap-6 items-center" data-testid="cancel-banner">
          <div className="md:col-span-9 flex gap-4">
            <AlertCircle className="w-6 h-6 text-[hsl(var(--primary))] shrink-0 mt-1" strokeWidth={1.5} />
            <div>
              <div className="label-eyebrow mb-1">Your membership is winding down</div>
              <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-2">
                You'll keep access until <em className="italic text-[hsl(var(--primary))]">{cancelDate.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</em>.
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">Nothing changes today — everything stays yours until then.</p>
            </div>
          </div>
          <div className="md:col-span-3 flex md:justify-end">
            <button onClick={reactivate} disabled={reactivating} data-testid="btn-reactivate" className="inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-white px-6 py-3 hover:bg-[hsl(var(--foreground))] transition-colors disabled:opacity-60">
              <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> {reactivating ? "…" : "Keep my rhythm"}
            </button>
          </div>
        </section>
      )}

      <section>
        <div className="label-eyebrow mb-4">
          {today?.is_before_launch ? `Program begins ${formatLongDate(today.program_start)}` : formatLongDate(activeDate || "")}
        </div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1] tracking-tight">
          Good to see you, <em className="italic text-[hsl(var(--primary))]">{user?.name?.split(" ")[0]}</em>.
        </h1>
        {user?.founding_member && (
          <div className="inline-flex items-center gap-2 mt-6 px-4 py-1.5 border border-[hsl(var(--primary))] text-[hsl(var(--primary))]" data-testid="founding-badge">
            <span className="font-script text-lg leading-none">✦ Founding member</span>
          </div>
        )}
        {today?.is_tester && !today?.is_before_launch && (
          <div className="inline-flex items-center gap-2 mt-6 ml-3 px-4 py-1.5 border border-[hsl(var(--secondary))] text-[hsl(var(--secondary))]" data-testid="tester-badge">
            <span className="font-script text-lg leading-none">✦ Early access</span>
          </div>
        )}
        {today?.month && (
          <p className="mt-6 text-stone-600 text-lg leading-relaxed max-w-2xl">
            You're in <strong className="font-medium">{today.month.label}</strong>
            {today.month.theme && <> — <em className="italic">{today.month.theme}</em></>}. One month at a time.
          </p>
        )}
      </section>

      {loading || !today ? (
        <div className="label-eyebrow">Loading today's practice…</div>
      ) : today.is_before_launch ? (
        <PreLaunchCountdown days={today.days_until_launch} />
      ) : (
        <section className="grid md:grid-cols-3 gap-0 border-t border-b border-border">
          <TodayCard
            icon={Leaf}
            title="Meal"
            line={dinner?.name || "No dinner scheduled yet"}
            sub="Tonight"
            doneKey="meal"
            date={activeDate}
            link={`/app/meals?date=${activeDate}`}
          />
          <TodayCard
            icon={Flame}
            title="Movement"
            line={movement?.title || "No movement scheduled"}
            sub={movement ? `${movement.duration_min} min · ${movement.focus}` : ""}
            doneKey="movement"
            date={activeDate}
            link={`/app/movement?date=${activeDate}`}
          />
          <TodayCard
            icon={HeartHandshake}
            title="Coaching"
            line={coaching?.title || "No session yet"}
            sub={coaching?.affirmation || ""}
            doneKey="coaching"
            date={activeDate}
            link={`/app/coaching?date=${activeDate}`}
          />
        </section>
      )}

      <section className="grid md:grid-cols-1 gap-0 border-b border-border">
        <SidePanel icon={Ruler} title="Track your body." body="Waist, hips, chest, arms, thighs. Not a scale — a landscape." link="/app/measurements" cta="Log measurements" testid="cta-measurements" />
      </section>

      {!today?.is_before_launch && (
        <section>
          <div className="label-eyebrow mb-4">Today's completion</div>
          <div className="flex items-baseline gap-4">
            <span className="font-serif text-6xl">{completed}<span className="text-stone-400">/3</span></span>
            <span className="text-stone-600">practices honoured</span>
          </div>
        </section>
      )}

      <section className="border-t border-b border-border grid md:grid-cols-12 gap-0" data-testid="membership-section">
        <div className="md:col-span-8 p-8 md:p-10 border-r border-border">
          <div className="label-eyebrow mb-4">Membership</div>
          {isSubscribed && user?.role !== "admin" ? (
            <>
              <h3 className="font-serif text-3xl mb-3 flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-[hsl(var(--primary))]" strokeWidth={1.5} /> Active
              </h3>
              <p className="text-stone-600 leading-relaxed max-w-lg mb-6">
                You're a member — every current and future monthly plan is yours. Manage payment, invoices, or cancel from the Stripe portal.
              </p>
              <button onClick={handleManageClick} disabled={managing} data-testid="btn-manage-subscription" className="inline-flex items-center gap-2 border border-[hsl(var(--foreground))] px-5 py-2.5 text-sm hover:bg-[hsl(var(--foreground))] hover:text-white transition-colors">
                <Settings className="w-4 h-4" strokeWidth={1.5} /> {managing ? "Opening…" : "Manage subscription"}
              </button>
            </>
          ) : user?.role === "admin" ? (
            <>
              <h3 className="font-serif text-3xl mb-3">Admin access</h3>
              <p className="text-stone-600 leading-relaxed max-w-lg">All plans unlocked as an admin. No membership required.</p>
            </>
          ) : (
            <>
              <h3 className="font-serif text-3xl mb-3">Not a member yet.</h3>
              <p className="text-stone-600 leading-relaxed max-w-lg mb-6">The base practice is yours. Monthly meal plans and archive live behind a small commitment.</p>
              <Link to="/pricing" data-testid="btn-upgrade" className="inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-white px-6 py-3 hover:bg-[hsl(var(--foreground))] transition-colors">
                See membership <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </>
          )}
        </div>
        <div className="md:col-span-4 p-8 md:p-10">
          <div className="label-eyebrow mb-2">This month</div>
          <div className="font-serif text-2xl leading-tight">{today?.month?.label || new Date().toLocaleString("default", { month: "long", year: "numeric" })}</div>
          <p className="text-sm text-stone-500 mt-2 italic">Each month brings a new theme.</p>
        </div>
      </section>

      {ref && (
        <section className="border-t border-b border-border grid md:grid-cols-12 gap-0" data-testid="referral-section">
          <div className="md:col-span-7 p-8 md:p-10 border-r border-border">
            <div className="label-eyebrow mb-4 flex items-center gap-2"><Gift className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} /> Bring a friend</div>
            <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-4">
              Every friend who joins earns you a <em className="italic text-[hsl(var(--primary))]">free month</em>.
            </h3>
            <p className="text-stone-600 leading-relaxed max-w-md mb-6">
              Share your code. When they subscribe, we credit $9.99 to your next Stripe invoice — quietly, automatically.
            </p>
            <div className="flex items-center gap-3 border border-border p-4 max-w-md">
              <div className="flex-1">
                <div className="label-eyebrow mb-1">Your code</div>
                <div className="font-serif text-2xl tracking-widest" data-testid="referral-code">{ref.code}</div>
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(referralLink); toast.success("Link copied."); }}
                data-testid="btn-copy-referral"
                className="inline-flex items-center gap-2 bg-[hsl(var(--foreground))] text-white px-4 py-2.5 text-xs uppercase tracking-widest hover:bg-[hsl(var(--primary))] transition-colors"
              >
                <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> Copy link
              </button>
            </div>
          </div>
          <div className="md:col-span-5 p-8 md:p-10 grid grid-cols-2 gap-6">
            <Stat label="Invited" value={ref.invited} />
            <Stat label="Converted" value={ref.converted} />
            <Stat label="Free months earned" value={ref.credits_count} />
            <Stat label="Value" value={`$${(ref.credit_total_cents / 100).toFixed(2)}`} />
          </div>
        </section>
      )}

    </div>
  );
}

function PreLaunchCountdown({ days }) {
  return (
    <section className="border-t border-b border-border p-10" data-testid="prelaunch-countdown">
      <div className="label-eyebrow mb-3">Countdown</div>
      <div className="flex items-baseline gap-6">
        <span className="font-serif text-7xl md:text-8xl">{days}</span>
        <span className="text-stone-600 text-lg">day{days === 1 ? "" : "s"} until <em className="italic text-[hsl(var(--primary))]">December 1, 2026</em></span>
      </div>
      <p className="mt-6 text-stone-600 leading-relaxed max-w-2xl">
        Your practice begins December 1, 2026. Every recipe, movement, and coaching session is being prepared — one month at a time.
      </p>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow mb-2">{label}</div>
      <div className="font-serif text-4xl">{value}</div>
    </div>
  );
}

function TodayCard({ icon: Icon, title, line, sub, doneKey, date, link }) {
  const { has, toggle } = useProgress();
  const done = has(date, doneKey);
  return (
    <div className="p-8 md:p-10 border-r last:border-r-0 border-border relative">
      <Icon className="w-6 h-6 text-[hsl(var(--primary))] mb-6" strokeWidth={1.25} />
      <div className="label-eyebrow mb-2">{title}</div>
      <h3 className="font-serif text-2xl leading-tight mb-3">{line}</h3>
      <p className="text-sm text-stone-600 leading-relaxed mb-8 italic">{sub}</p>
      <div className="flex items-center justify-between">
        <button
          onClick={() => toggle(date, doneKey)}
          data-testid={`today-toggle-${doneKey}`}
          className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${done ? "bg-[hsl(var(--secondary))] text-white border-[hsl(var(--secondary))]" : "border-border hover:border-stone-500"}`}
        >
          {done ? "Honoured" : "Mark done"}
        </button>
        <Link to={link} className="text-sm text-[hsl(var(--primary))] hover:underline flex items-center gap-1" data-testid={`today-open-${doneKey}`}>
          Open <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} />
        </Link>
      </div>
    </div>
  );
}

function SidePanel({ icon: Icon, title, body, link, cta, testid }) {
  return (
    <div className="p-8 md:p-12 border-r last:border-r-0 border-border">
      <Icon className="w-6 h-6 text-[hsl(var(--secondary))] mb-6" strokeWidth={1.25} />
      <h3 className="font-serif text-3xl mb-4">{title}</h3>
      <p className="text-stone-600 leading-relaxed max-w-md mb-6">{body}</p>
      <Link to={link} className="text-sm text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1" data-testid={testid}>
        {cta} <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} />
      </Link>
    </div>
  );
}
